import { useQuery } from "@tanstack/react-query";
import { BarChart3, ChevronRight, Clock, Copy, Globe, MailPlus } from "lucide-react";
import { Link, useParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorState, FullPageSpinner, Section, KeyValue, buttonClass } from "@/components/ui";
import { portal } from "@/lib/api";
import { periodLabel } from "@/lib/billing";
import { formatDate, formatMoney, formatTime } from "@/lib/format";
import { processorName } from "@/lib/payments";
import type { Invoice } from "@/lib/types";
import { BackLink, PageTitle, SectionTitle, Confirmation, TaskRow } from "@/components/billing/parts";

/** Figma "Billing — Payment successful · now on Growth (BIL-01)". */
export function CheckoutSuccessPage() {
  const { invoiceId = "" } = useParams();
  const { orgId, current } = useOrganization();
  const invoice = useQuery({
    queryKey: ["invoice", orgId, invoiceId],
    queryFn: () => portal.invoice(orgId, invoiceId),
  });
  const sub = useQuery({ queryKey: ["subscription", orgId], queryFn: () => portal.subscription(orgId) });

  if (invoice.isPending || sub.isPending) return <FullPageSpinner />;
  if (invoice.isError) return <ErrorState error={invoice.error} onRetry={() => void invoice.refetch()} />;
  if (sub.isError) return <ErrorState error={sub.error} onRetry={() => void sub.refetch()} />;

  const i = invoice.data;
  if (i.status === "pending") return <UpgradeOnItsWay invoice={i} organization={current.organization.name} />;
  const { account, upcoming_charge: next, payment_provider } = sub.data;
  const plan = account.plan;
  const paidAt = formatTime(i.paid_at ?? i.issued_at);
  const paid = formatMoney(i.total_cents, i.currency);
  const domains = plan.max_custom_domains;

  return (
    <div className="flex flex-col gap-6">
      <BackLink to="/billing" />
      <PageTitle
        title="Payment successful"
        subtitle={`${current.organization.name} is on ${plan.name} from today`}
      />
      <Confirmation title={`${paid} paid · confirmed by your bank at ${paidAt}`}>
        {processorName(payment_provider)} has confirmed the charge, so your {plan.name} limits are live now.
        {next && ` Your next charge is ${formatDate(next.date)}.`}
      </Confirmation>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <Section variant="compact" className="min-w-0 flex-1">
          <SectionTitle>Set the plan to work</SectionTitle>
          <div className="h-3" />
          <TaskRow
            icon={<Globe className="size-[18px]" aria-hidden />}
            title="Connect a custom domain"
            description={`${plan.name} includes connecting ${domains === 1 ? "one" : domains}. Buying a domain is a separate charge.`}
            action={<TaskLink to="/domains">Connect a domain</TaskLink>}
          />
          <TaskRow
            icon={<MailPlus className="size-[18px]" aria-hidden />}
            title="Create your first mailbox"
            description="hello@ on your domain, with professional mailboxes on your plan."
            action={<TaskLink to="/email">Create a mailbox</TaskLink>}
          />
          <TaskRow
            icon={<BarChart3 className="size-[18px]" aria-hidden />}
            title="Open your full analytics"
            description="The history recorded while you were on Free is now visible."
            action={<Soon />}
          />
        </Section>

        <Section variant="compact" className="w-full shrink-0 lg:w-[380px]">
          <SectionTitle size="md">Receipt</SectionTitle>
          <div className="h-3" />
          <KeyValue label="Invoice">{i.number}</KeyValue>
          <KeyValue label="Plan">
            {plan.name} · {periodLabel(i.billing_period)}
          </KeyValue>
          <KeyValue label="Paid today">{paid}</KeyValue>
          {i.payment_method_label && <KeyValue label="Payment method">{i.payment_method_label}</KeyValue>}
          {next && (
            <KeyValue label="Next charge">
              {formatDate(next.date)} · {formatMoney(next.total_cents, next.currency)}
            </KeyValue>
          )}
          <div className="h-3.5" />
          <a
            href={`/billing/invoices/${i.id}`}
            target="_blank"
            rel="noreferrer"
            className={buttonClass("secondary", "block")}
          >
            <Copy className="size-4" aria-hidden /> Download invoice
          </a>
          <div className="h-2.5" />
          <Link to="/billing" className={buttonClass("secondary", "block")}>
            Go to Billing
          </Link>
        </Section>
      </div>
    </div>
  );
}

/** Paid by Direct Debit: nothing has changed yet; the plan starts once the debit clears. No Figma
 * frame: it follows the payment-successful page with the receipt and next steps reworded. */
function UpgradeOnItsWay({ invoice: i, organization }: { invoice: Invoice; organization: string }) {
  const amount = formatMoney(i.total_cents, i.currency);
  const plan = i.plan ? i.plan.charAt(0).toUpperCase() + i.plan.slice(1) : "your new";
  return (
    <div className="flex flex-col gap-6">
      <BackLink to="/billing" />
      <PageTitle
        title="Upgrade on its way"
        subtitle={`${organization} moves to ${plan} once the Direct Debit clears`}
      />
      <p
        role="status"
        className="flex items-start gap-3 rounded-[14px] bg-[#fce7c6]/60 px-4 py-3.5 text-sm text-fg-muted"
      >
        <Clock className="mt-0.5 size-[18px] shrink-0 text-[#995c05]" aria-hidden />
        <span>
          <span className="block font-semibold text-fg">{amount} is being collected by Direct Debit</span>
          It can take up to 7 working days. Your {plan} plan starts as soon as the payment clears, and we'll
          let you know. Until then you stay on your current plan.
        </span>
      </p>

      <Section variant="compact" className="w-full lg:w-[380px]">
        <SectionTitle size="md">Receipt</SectionTitle>
        <div className="h-3" />
        <KeyValue label="Invoice">{i.number}</KeyValue>
        <KeyValue label="Plan">
          {plan} · {periodLabel(i.billing_period)}
        </KeyValue>
        <KeyValue label="Being collected">{amount}</KeyValue>
        {i.payment_method_label && <KeyValue label="Payment method">{i.payment_method_label}</KeyValue>}
        <div className="h-3.5" />
        <Link to="/billing" className={buttonClass("secondary", "block")}>
          Go to Billing
        </Link>
      </Section>
    </div>
  );
}

function TaskLink({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline"
    >
      {children} <ChevronRight className="size-[15px]" aria-hidden />
    </Link>
  );
}

const Soon = () => <span className="shrink-0 text-sm font-semibold text-fg-subtle">Coming soon</span>;
